import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useEffect, useRef, useState, useCallback } from "react";
import UserService from "../UserService";

export function useMessageConsumer(vhost) {
    const connectionRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [message, setMessage] = useState(null);
    const [error, setError] = useState(null);

    const invokeWithErrorReset = useCallback(async (method, ...args) => {
        try {
            const result = await connectionRef.current?.invoke(method, ...args);
            setError(null);
            return result;
        } catch (err) {
            setError(err.toString());
            throw err;
        }
    }, []);

    useEffect(() => {
        if (!vhost) return;

        // remove the /SD from the api url
        let baseURL = process.env.REACT_APP_API_URL.replace(/\/SD$/, "");
        let signalRUrl = baseURL + `/hubs/message-controller?vhost=${vhost}`;

        const connection = new HubConnectionBuilder()
            .withUrl(signalRUrl,{
                accessTokenFactory: async () => {
                    await UserService.updateToken(() => Promise.resolve());
                    return UserService.getToken();
                }
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        connection.on("MessageReceived", (msg) => setMessage(msg));
        connection.on("AckSuccess", () => {
            setMessage(null);
            setError(null);
        });
        connection.on("RequeueSuccess", () => {
            setMessage(null);
            setError(null);
        });
        connection.on("MoveSuccess", () => {
            setMessage(null);
            setError(null);
        });
        connection.on("MessageReleased", (err) => (setMessage(null), setError(err)));
        connection.on("Error", (err) => setError(err));

        connection.onreconnecting(() => setIsConnected(false));
        connection.onreconnected(() => setIsConnected(true));
        connection.onclose(() => setIsConnected(false));

        connection.start()
            .then(() => {
                setIsConnected(true);
                setError(null);
            })
            .catch((err) => setError(err.toString()));

        connectionRef.current = connection;

        return () => {
            connection.stop();
        };
    }, [vhost]);

    const startConsuming = useCallback((queueName) => {
        return invokeWithErrorReset("StartConsuming", queueName);
    }, [invokeWithErrorReset]);

    const ack = useCallback(() => {
        return invokeWithErrorReset("Ack");
    }, [invokeWithErrorReset]);

    const requeue = useCallback(() => {
        return invokeWithErrorReset("Requeue");
    }, [invokeWithErrorReset]);

    const moveMessage = useCallback((queueName) => {
        return invokeWithErrorReset("MoveMessage", queueName);
    }, [invokeWithErrorReset]);

    const filterMessages = useCallback((queueName, headers) => {
        return invokeWithErrorReset("FilterOutMessage", queueName, headers);
    }, [invokeWithErrorReset]);

    const resetMessage = useCallback(() => {
        setMessage(null);
    }, []);

    return { isConnected, message, error, startConsuming, ack, requeue, moveMessage, filterMessages, resetMessage };
}